import React, { useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { NewsletterTemplateSummary } from '../../../types/models/NewsletterTemplate';
import type { ApiResponseNewsletterTemplateSingle } from '../../../types/api/ApiResponseNewsletterTemplate';

interface NewsletterTemplateArchiveModalProps {
  template: NewsletterTemplateSummary;
  onClose: () => void;
  /** Drop the archived row from the (active) list. */
  onArchived: (id: number) => void;
}

/**
 * Confirm-and-archive modal for a newsletter template. `DELETE` is a SOFT delete: the backend stamps
 * `archived_at` (idempotent) instead of removing the row, so a template referenced by a past or
 * in-flight send is never physically destroyed. There is no restore endpoint, so this is one-way
 * from the UI — the copy makes that clear without implying permanent data loss.
 */
function NewsletterTemplateArchiveModal({ template, onClose, onArchived }: NewsletterTemplateArchiveModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [error, setError] = useState<string | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = async () => {
    setIsArchiving(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/newsletters/templates/${template.id}`, { method: 'DELETE' });
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseNewsletterTemplateSingle | null;

      if (body?.status === 'success' && body.data?.newsletter_template) {
        addNotification({
          title: 'Template archived',
          srTitle: 'Success alert',
          description: `${template.name} was archived and will no longer be offered for new sends.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onArchived(template.id);
        onClose();
        return;
      }

      const message = body?.message ?? 'Could not archive the template. Please try again.';
      setError(message);
      addNotification({
        title: 'Could not archive template',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'Could not archive template',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <Modal variant="small" isOpen onClose={onClose}>
      <ModalHeader title="Archive template" titleIconVariant="warning" />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="Archive failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content>
          Archive <strong>{template.name}</strong>? It will move to the archived set and stop being offered for new
          newsletter sends. Nothing is deleted — the template is kept so past sends still resolve, and you can still
          edit it from the Archived view.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleArchive()} isLoading={isArchiving} isDisabled={isArchiving}>
          Archive
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isArchiving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default NewsletterTemplateArchiveModal;
